import Point from "@arcgis/core/geometry/Point";
import { addressToLocations, locationToAddress, suggestLocations } from "@arcgis/core/rest/locator";
import { ALPHA3_TO_ALPHA2_COUNTRY_CODES } from "@src/constants/country-codes";
/** Minimum valid value for latitude */
export const LATITUDE_MIN = -90;

/** Maximum valid value for latitude */
export const LATITUDE_MAX = 90;

/** Minimum valid value for longitude */
export const LONGITUDE_MIN = -180;

/** Maximum valid value for longitude */
export const LONGITUDE_MAX = 180;

/** Number of decimal places kept for latitude/longitude in the deep link. */
export const COORDINATE_PRECISION = 5;

/** Base URL for the ArcGIS geocoding service */
const ARCGIS_GEOCODE_SERVICE_URL = "https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer";

/**
 * Language codes used by ArcGIS geocoding service.
 * ArcGIS service supports language codes based on ISO 639-1. More codes can be added if needed.
 * https://developers.arcgis.com/rest/geocode/geocode-coverage/#supported-language-codes
 */
export const ARCGIS_LANGUAGE_CODES = ["ES", "DE", "JA", "FR", "KO", "EN", "PT", "IT", "ZH"];

/** Map of language codes supported by our application to the corresponding codes used by ArcGIS service */
export const ARCGIS_LANGUAGE_CODES_MAP = {
  es: "ES",
  de: "DE",
  ja: "JA",
  fr: "FR",
  ko: "KO",
  en: "EN",
  pt: "PT",
  it: "IT",
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Name follows a ISO format.
  "zh-Hans": "ZH"
};

/** Represents the language codes supported by the ArcGIS geocoding service */

/** Represents a suggested address from the geocoding service */

/** An ArcGIS location entity with defined latitude and longitude */

/**
 * The base attributes of an ArcGIS address:
 * https://developers.arcgis.com/rest/geocode/service-output/#output-fields
 */

/** The address attributes of an ArcGIS address candidate */

/** The address attributes of an ArcGIS reverse geocode response */

/** An ArcGIS address candidate */

/** Represents a geocoded address entity */

/** Properties for the getAddressSuggestions function */

/** Properties for the geocodeAddress function */

/**
 * @returns A list of address suggestions for the given input text using the geocoding service.
 * @throws An error if the request to the geocoding service fails.
 */
export async function getAddressSuggestions({
  text,
  signal
}) {
  const rawSuggestions = await suggestLocations(ARCGIS_GEOCODE_SERVICE_URL, {
    text,
    // The ArcGIS locator interface has the location field as required but the API endpoint
    // actually has it as optional: https://developers.arcgis.com/rest/geocode/suggest/#location
    // Location is used to bias the suggestions around the pass location, but for our use case
    // we do not want to bias the suggestions around any location, so we leave it as undefined.
    location: undefined
  }, {
    // Prevent suggestion collections from being returned, we only want single suggestions/addresses
    // https://developers.arcgis.com/rest/geocode/suggest/#returncollections
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Name given by ArcGIS API
    query: {
      returnCollections: false
    },
    signal
  });
  return rawSuggestions.filter(isValidSuggestionResult).map(suggestion => ({
    label: suggestion.text,
    key: suggestion.magicKey
  }));
}

/**
 * @returns The geocoded address entity for the given address suggestion using the geocoding service.
 * @throws An error if the request to the geocoding service fails or if the response doesn't contain a valid geocoded address.
 */
export async function geocodeAddress({
  addressSuggestion,
  langCode,
  signal
}) {
  /**
   * This is the workflow suggested by ArcGIS: https://developers.arcgis.com/rest/geocode/find-address-candidates/#magickey
   * 1. Get suggestions from the /suggest endpoint. This is handled by the getAddressSuggestions function.
   * 2. Once the user selects an address suggestion, request its corresponding geocoded address candidate
   * via the /findAddressCandidates endpoint with "magicKey" and "singleLine" parameters.
   */
  const candidates = await addressToLocations(ARCGIS_GEOCODE_SERVICE_URL, {
    address: {
      singleLine: addressSuggestion.label
    },
    magicKey: addressSuggestion.key,
    maxLocations: 1
  }, {
    signal,
    query: {
      langCode,
      // Set outFields param so that the response includes the required fields to populate the
      // postalAddress property: https://developers.arcgis.com/rest/geocode/service-output/#output-fields
      outFields: "City,CntryName,Country,Postal,Region,StAddr,LongLabel"
    }
  });
  const candidate = candidates[0];
  if (!candidate || !isAddressCandidate(candidate)) {
    throw new Error("Failed to geocode address suggestion: No valid candidates found");
  }
  return {
    // "LongLabel" field has preference over the "address" field since it normally contains more information.
    address: candidate.attributes.LongLabel || candidate.address,
    position: {
      lat: candidate.location.latitude,
      lng: candidate.location.longitude
    },
    postalAddress: {
      streetAddress: candidate.attributes.StAddr,
      locality: candidate.attributes.City,
      region: candidate.attributes.Region,
      postalCode: candidate.attributes.Postal,
      country: candidate.attributes.CntryName,
      // ArcGIS usually returns an ISO 3166-1 alpha-3 code (e.g. "DEU"),
      // but our data model uses alpha-2 (e.g. "DE"), so we convert it here.
      // If the conversion fails, we fallback to the raw value returned by ArcGIS.
      countryCode: toAlpha2CountryCode(candidate.attributes.Country) ?? candidate.attributes.Country
    }
  };
}

/**
 * Uses the geocoding service to reverse geocode a GPS position into a human-readable address.
 * @returns The full address for the given GPS position, including other address attributes if available.
 * @param position The GPS position to reverse geocode.
 * @param signal An optional AbortSignal to cancel the request.
 * @throws An error if the request to the geocoding service fails.
 */
export async function reverseGeocode(position, signal) {
  const point = new Point({
    longitude: position.lng,
    latitude: position.lat
  });
  const response = await locationToAddress(ARCGIS_GEOCODE_SERVICE_URL, {
    location: point
  }, {
    signal
  });
  const address = response.address;
  const attributes = response.attributes;
  if (!isReverseGeocodeAttributes(attributes)) {
    throw new Error("Failed to reverse geocode position: No valid candidates found");
  }
  return {
    // "LongLabel" is the complete address, but if it's not available we fallback to the "Address" field.
    address: attributes.LongLabel || address || "",
    position: {
      lat: position.lat,
      lng: position.lng
    },
    postalAddress: {
      streetAddress: attributes.Address,
      locality: attributes.City,
      region: attributes.Region,
      postalCode: attributes.Postal,
      country: attributes.CntryName,
      // ArcGIS usually returns an ISO 3166-1 alpha-3 code (e.g. "DEU"),
      // but our data model uses alpha-2 (e.g. "DE"), so we convert it here.
      // If the conversion fails, we fallback to the raw value returned by ArcGIS.
      countryCode: toAlpha2CountryCode(attributes.CountryCode) ?? attributes.CountryCode
    }
  };
}

/**
 * @returns Whether the given error is an AbortError caused by aborting a geocoding request.
 * @param error The error to check.
 */
export function isGeocodeAbortError(error) {
  // The geocoding service we use, ArcGIS, returns a custom error object with the name
  // property set to "AbortError" when a request is aborted:
  // https://developers.arcgis.com/javascript/latest/async-cancellation-with-abortcontroller
  return typeof error === "object" && error !== null && "name" in error && typeof error.name === "string" && error.name === "AbortError";
}

/**
 * @returns Whether the ArcGIS suggestion result has defined text and magicKey properties.
 * @param value The ArcGIS suggestion result to check.
 */
export function isValidSuggestionResult(value) {
  return typeof value.text === "string" && typeof value.magicKey === "string";
}

/**
 * Converts an ISO 3166-1 alpha-3 country code (as returned by ArcGIS) to its alpha-2 equivalent.
 * @returns The alpha-2 country code, or undefined if the input is missing or not a known alpha-3 code.
 * @param alpha3 The alpha-3 country code to convert.
 */
export function toAlpha2CountryCode(alpha3) {
  if (!alpha3) {
    return undefined;
  }
  return ALPHA3_TO_ALPHA2_COUNTRY_CODES[alpha3.toUpperCase()];
}

/**
 * @returns Whether the given value is an object with defined latitude and longitude properties.
 * @param value The value to check.
 */
function isAddressCandidateLocation(value) {
  return typeof value === "object" && value !== null && "latitude" in value && typeof value.latitude === "number" && "longitude" in value && typeof value.longitude === "number";
}

/**
 * @returns Whether the given value is an object with defined base address attributes properties.
 * @param value The value to check.
 */
function isBaseAddressAttributes(value) {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if ("LongLabel" in value && typeof value.LongLabel !== "string") {
    return false;
  }
  if ("City" in value && typeof value.City !== "string") {
    return false;
  }
  if ("Region" in value && typeof value.Region !== "string") {
    return false;
  }
  if ("Postal" in value && typeof value.Postal !== "string") {
    return false;
  }
  if ("CntryName" in value && typeof value.CntryName !== "string") {
    return false;
  }
  return true;
}

/**
 * @returns Whether the given value is an object with defined address candidate attributes properties.
 * @param value The value to check.
 */
function isAddressCandidateAttributes(value) {
  if (!isBaseAddressAttributes(value)) {
    return false;
  }
  if ("StAddr" in value && typeof value.StAddr !== "string") {
    return false;
  }
  if ("Country" in value && typeof value.Country !== "string") {
    return false;
  }
  return true;
}

/**
 * @returns Whether the given value is a valid reverse geocode response with defined address attributes.
 * @param value The value to check.
 */
function isReverseGeocodeAttributes(value) {
  if (!isBaseAddressAttributes(value)) {
    return false;
  }
  if ("Address" in value && typeof value.Address !== "string") {
    return false;
  }
  if ("CountryCode" in value && typeof value.CountryCode !== "string") {
    return false;
  }
  return true;
}

/**
 * @returns Whether the given ArcGIS address candidate has defined address, latitude, longitude and attributes properties.
 * @param value The ArcGIS address candidate to check.
 */
export function isAddressCandidate(value) {
  return typeof value === "object" && value !== null && "address" in value && typeof value.address === "string" && "location" in value && isAddressCandidateLocation(value.location) && "attributes" in value && isAddressCandidateAttributes(value.attributes);
}